import React from "react";
import { CheckIcon, ChevronDownIcon } from "@radix-ui/react-icons";
import * as SelectPrimitive from "@radix-ui/react-select";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";
import {
  border,
  color,
  flexbox,
  layout,
  position,
  shadow,
  space,
  typography,
} from "styled-system";
import { Col, Row } from "@/styles/layout";
import { Body } from "@/styles/typography";

const Trigger = styled(SelectPrimitive.SelectTrigger)`
  all: unset;
  ${color};
  ${space};
  ${layout};
  ${flexbox};
  ${position};
  ${border};
  ${typography};
  ${shadow};

  &:hover {
    background-color: ${themeGet("color.elementHover")};
    border-color: ${themeGet("color.borderHover")};
  }
`;
const Content = styled(SelectPrimitive.Content)<any>`
  all: unset;
  ${color};
  ${space};
  ${layout};
  ${flexbox};
  ${position};
  ${border};
  ${typography};
  ${shadow};
`;

const ViewPort = styled(SelectPrimitive.Viewport)<any>`
  ${color};
  ${space};
  ${layout};
  ${flexbox};
  ${position};
  ${border};
  ${typography};
  ${shadow};
`;

const Item = styled(SelectPrimitive.Item)<any>`
  all: unset;
  ${color};
  ${space};
  ${layout};
  ${flexbox};
  ${position};
  ${border};
  ${typography};
  ${shadow};

  display: flex;
  align-items: center;
  height: 25px;

  &:hover {
    background-color: ${themeGet("color.elementHover")};
  }
`;

const ItemIndicator = styled(SelectPrimitive.ItemIndicator)<any>`
  ${color};
  ${space};
  ${layout};
  ${flexbox};
  ${position};
  ${border};
  ${typography};
  ${shadow};
`;

// Exports

const defaultTriggerStyle = {
  color: "textLow",
  fontSize: "2",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderColor: "primary.borderSubtle",
  borderWidth: "1px",
  borderStyle: "solid",
  borderRadius: "8px",
  height: 56,
  width: "100%",
  boxSizing: "border-box",
};

export interface SelectItem {
  value: string;
  label: string;
}
interface Props {
  label?: string;
  items: { value: string; label: string }[];
  value: string | undefined;
  onChange: (v: string) => void;
  triggerStyle?: any;
}

const Select = ({ label, items, value, onChange, triggerStyle }: Props) => {
  return (
    <SelectPrimitive.Root value={value} onValueChange={onChange}>
      {label && (
        <label
          style={{
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          <Body
            color={"primary.textHigh"}
            fontSize="12px"
            fontWeight="400"
            mb="4px"
          >
            {label.toUpperCase()}
          </Body>
        </label>
      )}
      <Trigger {...defaultTriggerStyle} {...triggerStyle} position="relative">
        <Col alignItems="stretch" width="100%">
          <Row
            alignItems="center"
            px="16px"
            justifyContent={"space-between"}
            width="100%"
          >
            <SelectPrimitive.Value />
            <SelectPrimitive.Icon>
              <ChevronDownIcon />
            </SelectPrimitive.Icon>
          </Row>
        </Col>
      </Trigger>
      <SelectPrimitive.Content>
        <Col
          bg="primary.bgBase"
          p="16px"
          borderRadius="8px"
          overflow="hidden"
          boxShadow="l"
          maxHeight={"300px"}
          overflowY={"auto"}
          width="100%"
        >
          {items.map((item) => (
            <Item
              key={item.value}
              value={item.value}
              color="primary.textHigh"
              justifyContent="space-between"
              alignItems="center"
              height="24px"
              fontSize="14px"
              zIndex="100"
              py="8px"
            >
              <SelectPrimitive.ItemText>
                {item.label ?? item.value}
              </SelectPrimitive.ItemText>
              <ItemIndicator ml="2">
                <CheckIcon />
              </ItemIndicator>
            </Item>
          ))}
        </Col>
      </SelectPrimitive.Content>
    </SelectPrimitive.Root>
  );
};
export default Select;
